/**
 * Writing help in the editor: with text selected, a ✨ button (or /AI at the
 * cursor) opens a menu of rewrites. Claude's answer streams in, is shown as a
 * word diff against the selection, and only replaces it when accepted.
 */
import { useEffect, useRef, useState, type CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';
import { useAiWrite, type WriteTarget as Target } from './writeState';
import type { Editor } from '@tiptap/core';
import { Check, Loader2, RotateCcw, Sparkles, X } from 'lucide-react';
import {
  AiError,
  diffWords,
  INSERT_ACTIONS,
  instructionFor,
  markedNote,
  runWrite,
  SEL_END,
  SEL_START,
  WRITE_ACTIONS,
  writeMessage,
  type AiErrorCode,
} from '@nexus/ai';
import { ensureTaskIds } from '@nexus/editor';
import { parseMarkdown, serializeMarkdown } from '@nexus/markdown';
import type { JSONContent } from '@tiptap/core';
import { cn } from '../lib/cn';
import { transport, useAiChat } from './chat';

/** The note as Markdown with the selection marked, and the selection itself. */
function noteWithSelection(editor: Editor, from: number, to: number) {
  const { state } = editor;
  const tr = state.tr.insertText(SEL_END, to).insertText(SEL_START, from);
  const marked = serializeMarkdown(tr.doc.toJSON() as JSONContent);
  const start = marked.indexOf(SEL_START);
  const end = marked.indexOf(SEL_END, start);
  const plain = marked.replace(SEL_START, '').replace(SEL_END, '');
  const note = markedNote(plain, start, end - 1);
  const selected =
    from === to
      ? ''
      : serializeMarkdown({
          type: 'doc',
          content: state.doc.slice(from, to).content.toJSON() as JSONContent[],
        }).trim();
  return { note, selected };
}

/** Replace the selection with Claude's Markdown (inline when it stays one paragraph). */
function apply(editor: Editor, from: number, to: number, markdown: string) {
  const doc = ensureTaskIds(parseMarkdown(markdown).doc).doc;
  const blocks = doc.content ?? [];
  const $from = editor.state.doc.resolve(from);
  const $to = editor.state.doc.resolve(to);
  const inline =
    $from.parent.isTextblock &&
    $from.sameParent($to) &&
    blocks.length === 1 &&
    blocks[0]!.type === 'paragraph' &&
    // Writing into an empty paragraph: insert whole blocks.
    $from.parent.content.size > 0;
  editor
    .chain()
    .focus()
    .insertContentAt({ from, to }, inline ? (blocks[0]!.content ?? []) : blocks)
    .run();
}

const menu =
  'fixed z-[60] flex w-[min(28rem,calc(100vw-2rem))] flex-col gap-2 rounded-xl border border-border bg-bg p-2 text-sm shadow-pop';

/** Writing help for the editor of this page (rendered by PageView). */
export function AiWrite({
  editor,
  title,
  hidden,
}: {
  editor: Editor | null;
  title: string;
  /** The page is hidden from AI: nothing may be sent. */
  hidden: boolean;
}) {
  const target = useAiWrite((s) => (s.target?.editor === editor ? s.target : null));
  if (!editor || !target) return null;
  return (
    <WritePopover
      key={`${target.from}-${target.to}`}
      target={target}
      title={title}
      hidden={hidden}
    />
  );
}

type Stage =
  | { kind: 'pick' }
  | { kind: 'running'; text: string }
  | { kind: 'result'; text: string }
  | { kind: 'error'; code: AiErrorCode };

function WritePopover({
  target,
  title,
  hidden,
}: {
  target: Target;
  title: string;
  hidden: boolean;
}) {
  const { t } = useTranslation();
  const close = useAiWrite((s) => s.close);
  const { editor } = target;
  const [stage, setStage] = useState<Stage>({ kind: 'pick' });
  const [custom, setCustom] = useState('');
  const range = useRef({ from: target.from, to: target.to });
  const lastInstruction = useRef('');
  const abort = useRef<AbortController | null>(null);
  const input = useRef<HTMLInputElement>(null);
  const selecting = target.from !== target.to;
  const [original] = useState(() =>
    selecting ? noteWithSelection(editor, target.from, target.to).selected : '',
  );

  // Keep the range on the same text while the document changes (sync, typing).
  useEffect(() => {
    const onTx = ({
      transaction,
    }: {
      transaction: { mapping: { map(p: number, a?: number): number } };
    }) => {
      range.current = {
        from: transaction.mapping.map(range.current.from, -1),
        to: transaction.mapping.map(range.current.to, 1),
      };
    };
    editor.on('transaction', onTx);
    return () => {
      editor.off('transaction', onTx);
      abort.current?.abort();
    };
  }, [editor]);
  useEffect(() => {
    input.current?.focus();
  }, []);

  const run = async (instruction: string) => {
    if (!instruction.trim()) return;
    lastInstruction.current = instruction;
    abort.current?.abort();
    const controller = new AbortController();
    abort.current = controller;
    setStage({ kind: 'running', text: '' });
    try {
      const { note } = noteWithSelection(editor, range.current.from, range.current.to);
      const text = await runWrite({
        transport,
        model: 'sonnet',
        message: writeMessage({ title, note, instruction }),
        signal: controller.signal,
        onText: (soFar) => setStage({ kind: 'running', text: soFar }),
        onUsage: (usage) => useAiChat.setState({ usage }),
      });
      setStage({ kind: 'result', text });
    } catch (e) {
      if (controller.signal.aborted) return;
      if (!(e instanceof AiError)) console.error('ai write', e);
      setStage({ kind: 'error', code: e instanceof AiError ? e.code : 'api' });
    }
  };

  const accept = (text: string) => {
    apply(editor, range.current.from, range.current.to, text);
    close();
  };
  const dismiss = () => {
    abort.current?.abort();
    close();
    editor.commands.focus();
  };

  // Below the selection, kept inside the window.
  const coords = editor.view.coordsAtPos(target.to);
  const style: CSSProperties = {
    top: Math.min(coords.bottom + 8, window.innerHeight - 320),
    left: Math.max(8, Math.min(coords.left - 16, window.innerWidth - 470)),
  };
  const actions = selecting ? WRITE_ACTIONS : INSERT_ACTIONS;

  return (
    <div
      role="dialog"
      aria-label={t('ai.write.title')}
      className={menu}
      style={style}
      onKeyDown={(e) => {
        if (e.key === 'Escape') {
          e.preventDefault();
          dismiss();
        }
      }}
    >
      <div className="flex items-center gap-2 text-xs text-fg-muted">
        <Sparkles className="size-3.5 text-accent-ink" />
        <span className="flex-1">{t('ai.write.title')}</span>
        <button
          type="button"
          onClick={dismiss}
          aria-label={t('ai.write.discard')}
          className="rounded p-0.5 hover:bg-bg-hover"
        >
          <X className="size-3.5" />
        </button>
      </div>

      {hidden ? (
        <p className="text-fg-muted">{t('ai.write.hidden')}</p>
      ) : stage.kind === 'pick' ? (
        <>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              void run(custom);
            }}
          >
            <input
              ref={input}
              value={custom}
              onChange={(e) => setCustom(e.target.value)}
              placeholder={selecting ? t('ai.write.customSelection') : t('ai.write.customInsert')}
              aria-label={t('ai.write.instruction')}
              className="w-full rounded-lg border border-border bg-bg px-2 py-1.5 outline-none focus:border-accent"
            />
          </form>
          <div className="flex flex-wrap gap-1" role="group" aria-label={t('ai.write.actions')}>
            {actions.map((a) => (
              <button
                key={a}
                type="button"
                onClick={() => void run(instructionFor(a))}
                className="rounded-lg border border-border px-2 py-1 text-xs hover:bg-bg-hover"
              >
                {t(`ai.write.action.${a}`)}
              </button>
            ))}
          </div>
        </>
      ) : stage.kind === 'error' ? (
        <>
          <p role="alert" className="rounded-md bg-red-500/10 px-2 py-1.5 text-danger">
            {t(`ai.errors.${stage.code}`)}
          </p>
          <div className="flex justify-end">
            <button
              type="button"
              onClick={() => void run(lastInstruction.current)}
              className="rounded-lg border border-border px-2 py-1 text-xs hover:bg-bg-hover"
            >
              {t('ai.write.again')}
            </button>
          </div>
        </>
      ) : (
        <>
          <div
            className="max-h-64 overflow-y-auto rounded-lg border border-border bg-bg-subtle px-2 py-1.5 whitespace-pre-wrap"
            data-testid="ai-write-result"
            aria-live="polite"
          >
            {stage.kind === 'running' && !stage.text ? (
              <span className="flex items-center gap-2 text-xs text-fg-muted">
                <Loader2 className="size-3.5 animate-spin" />
                {t('ai.thinking')}
              </span>
            ) : stage.kind === 'result' && selecting ? (
              diffWords(original, stage.text).map((p, i) => (
                <span
                  key={i}
                  className={cn(
                    p.kind === 'added' && 'bg-green-500/15 text-green-700 dark:text-green-300',
                    p.kind === 'removed' && 'bg-red-500/10 text-danger line-through',
                  )}
                >
                  {p.text}
                </span>
              ))
            ) : (
              stage.text
            )}
          </div>
          {stage.kind === 'result' && (
            <div className="flex justify-end gap-1">
              <button
                type="button"
                onClick={dismiss}
                className="rounded-md px-2 py-1 text-xs text-fg-muted hover:bg-bg-hover"
              >
                {t('ai.write.discard')}
              </button>
              <button
                type="button"
                onClick={() => void run(lastInstruction.current)}
                className="flex items-center gap-1 rounded-lg border border-border px-2 py-1 text-xs hover:bg-bg-hover"
              >
                <RotateCcw className="size-3" />
                {t('ai.write.again')}
              </button>
              <button
                type="button"
                autoFocus
                onClick={() => accept(stage.text)}
                className="flex items-center gap-1 rounded-full bg-accent px-2 py-1 text-xs font-medium text-accent-fg"
              >
                <Check className="size-3" />
                {selecting ? t('ai.write.replace') : t('ai.write.insert')}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export function AiSelectionButton({ editor }: { editor: Editor | null }) {
  const { t } = useTranslation();
  const open = useAiWrite((s) => s.open);
  const writing = useAiWrite((s) => !!s.target);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);

  useEffect(() => {
    if (!editor) return;
    const update = () => {
      const { empty, from } = editor.state.selection;
      if (empty || !editor.isFocused) return setPos(null);
      const c = editor.view.coordsAtPos(from);
      setPos({ top: Math.max(4, c.top - 34), left: Math.max(4, c.left) });
    };
    const hide = () => setTimeout(() => !editor.isFocused && setPos(null), 150);
    editor.on('selectionUpdate', update);
    editor.on('focus', update);
    editor.on('blur', hide);
    return () => {
      editor.off('selectionUpdate', update);
      editor.off('focus', update);
      editor.off('blur', hide);
    };
  }, [editor]);

  if (!editor || !pos || writing) return null;
  return (
    <button
      type="button"
      // Keep the selection: don't take focus from the editor.
      onMouseDown={(e) => e.preventDefault()}
      onClick={() => open(editor)}
      style={{ top: pos.top, left: pos.left }}
      className="fixed z-[55] flex items-center gap-1 rounded-lg border border-border bg-bg px-2 py-1 text-xs shadow-md hover:bg-bg-hover"
    >
      <Sparkles className="size-3.5 text-accent-ink" />
      {t('ai.write.button')}
    </button>
  );
}
